import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Eye, EyeOff } from 'lucide-react';
import { t, tr } from '@/i18n';
import { api, errorField, errorMessage } from '@/lib/api';
import { useSetMe } from '@/lib/auth';
import type { AuthResponse } from '@/lib/types';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AuthLayout } from './AuthLayout';
import { OAuthButtons } from './OAuthButtons';

export function safeNext(next: string | null): string {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/dashboard';
}

export function PasswordInput(props: { value: string; onChange: (v: string) => void; label: string; error?: string | null; autoComplete: string; hint?: string }) {
  const [show, setShow] = useState(false);
  return (
    <Input
      label={props.label}
      type={show ? 'text' : 'password'}
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
      autoComplete={props.autoComplete}
      error={props.error}
      hint={props.hint}
      required
      trailing={
        <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? t('auth.hidePassword') : t('auth.showPassword')} className="text-fg-subtle hover:text-fg [&_svg]:size-4">
          {show ? <EyeOff /> : <Eye />}
        </button>
      }
    />
  );
}

export function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setMe = useSetMe();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Ошибка входа через GitHub/Google приходит параметром ?error= после редиректа.
  const [error, setError] = useState<{ message: string; field: string | null } | null>(() => {
    const e = params.get('error');
    return e ? { message: tr(e), field: null } : null;
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await api<AuthResponse>('/api/auth/login', { body: { email, password } });
      setMe(res);
      navigate(safeNext(params.get('next')), { replace: true });
    } catch (err) {
      setError({ message: errorMessage(err), field: errorField(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={t('auth.loginTitle')}
      subtitle={t('auth.loginSubtitle')}
      footer={
        <>
          {t('auth.noAccount')}{' '}
          <Link to={`/register${params.get('next') ? `?next=${encodeURIComponent(params.get('next')!)}` : ''}`} className="font-medium text-accent-fg hover:underline">
            {t('auth.register')}
          </Link>
        </>
      }
    >
      <OAuthButtons next={params.get('next')} />
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Input label={t('auth.email')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required autoFocus error={error?.field === 'email' ? error.message : null} />
        <PasswordInput label={t('auth.password')} value={password} onChange={setPassword} autoComplete="current-password" />
        <div className="-mt-1 flex justify-end">
          <Link to="/forgot-password" className="text-xs font-medium text-fg-muted hover:text-fg">
            {t('auth.forgot')}
          </Link>
        </div>
        {error && error.field !== 'email' && (
          <p role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger dark:bg-danger/15">
            {error.message}
          </p>
        )}
        <Button type="submit" variant="primary" size="lg" loading={loading}>
          {t('auth.login')}
        </Button>
      </form>
    </AuthLayout>
  );
}
